import Debug from "debug";
import { GetModelAttributes, List, Model, Query, SubscriptionCallbackPayload } from "./";

const debug = Debug("subscribeList");

type OnListUpdateProps<M extends Model<any>> = SubscriptionCallbackPayload<M> & {
  list: List<M>;
};
export type OnListUpdate<M extends Model<any>> = (props: OnListUpdateProps<M>) => void;

async function insertToList<M extends Model<any>>(list: List<M>, model: M, query: Query<M, any>) {
  if (query.hasPreloads) {
    await query.loadPreloads([model]);
  }

  list.insert(model);
}

export function subscribeListHandler<M extends Model<any>>(
  list: List<M>,
  query: Query<M, any>,
  data: SubscriptionCallbackPayload<M>,
  callback?: OnListUpdate<M>,
) {
  const callCallback = (type: typeof data.type) => {
    if (!callback) return;
    // @ts-ignore
    callback({
      ...data,
      type,
      list,
    });
  };

  switch (data.type) {
    case "INSERT":
      if (query.matches(data.model)) {
        debug("inserting", query.tableName, data.model.id);
        insertToList(list, data.model, query);
        callCallback(data.type);
      }
      break;
    case "UPDATE":
      const listIndex = list.findIndexById(data.model.uniqueId);

      if (listIndex >= 0) {
        if (!query.matches(data.model)) {
          debug("update caused remove", query.tableName, data.model.id);
          list.remove(data.model);
          callCallback("DELETE");
        } else {
          debug("updating", query.tableName, data.model.id);
          const item = list[listIndex];
          item.updateFromSubscription(data.model.rawAttributes, data.source);
          list.resort();
          callCallback("UPDATE");
        }
      } else if (query.matches(data.model)) {
        debug("update caused insert", query.tableName, data.model.id);
        insertToList(list, data.model, query);
        callCallback("INSERT");
      } else {
        debug("update not in query", query.tableName, data.model.id);
      }
      break;
    case "DELETE":
      debug("removing", query.tableName, data.model.id);
      list.remove(data.model);
      callCallback("DELETE");
      break;
  }
}

interface SubscribeListOptions<M extends Model<any>> {
  filter?: Partial<GetModelAttributes<M>>;
  callback?: OnListUpdate<M>;
  source?: Parameters<Query<M, any>["subscribe"]>[0]["source"];
}

/**
 * Subscribe a list to a query. When realtime changes are made, they are checked
 * against the query and if they match then the list is mutated.
 *
 * Example usage with a query:
 *
 * ```typescript
 *   const query = Club.query().where({ active: true });
 *   const list = new List(query);
 *   const unsubscribe = await subscribeList(list, query);
 * ```
 *
 * Example usage with association:
 *
 * ```typescript
 *   const club = Club.query().find('123');
 *   await club.players.load();
 *   const list = new List(...club.players);
 *   const unsubscribe = await subscribeList(list, club.players.query);
 * ```
 *
 * Example use in a controller:
 *
 * ```typescript
 *   this.addDependency(await subscribeList(list, query));
 * ```
 *
 * Example with custom callback:
 *
 * ```typescript
 *   const callback = ({list, type, model, source}) => {
 *     console.log('list updated', type, model.id, source);
 *   }
 *   await subscribeList(list, query, { callback });
 * ```
 *
 * Example with source filter:
 *
 * ```typescript
 *   await subscribeList(list, query, { source: 'client' });
 * ```
 */
export function subscribeList<M extends Model<any>>(
  list: List<M>,
  query: Query<M, any>,
  options: SubscribeListOptions<M> = {},
) {
  return query.subscribe({
    callback: (data) => {
      for (const item of data) {
        subscribeListHandler<M>(list, query, item, options.callback);
      }
    },
    filter: options.filter,
    source: options.source,
  });
}
